import { cn } from '@evogard/ui';
import type { ManagementClaim } from '@evogard/contract';
import { analysisHoldLabel, isManagementBlocking } from '@evogard/domain';
import {
  LINE_LABEL,
  LINE_TONE,
  ageText,
  daysSince,
  initialsOf,
  numberOf,
  situationLabel,
  stageLabel,
} from './managementLabels.js';

/** "Entrou agora": aberto nas últimas 24 horas — a bolinha nasce destacada. */
const FRESH_MS = 24 * 3_600_000;

/** A frase que descreve o acionamento: a dica, e o nome acessível da bolinha. */
export function describeClaim(c: ManagementClaim): string {
  const who =
    c.source === 'periferico'
      ? 'Periféricos'
      : c.type === 'terceiro'
        ? `Terceiro do #${c.parentClaimId}`
        : 'Associado';
  const where = [
    `${LINE_LABEL[c.line]}, em ${stageLabel(c.line, c.phase)}`,
    c.situation && !isManagementBlocking(c.line, c.situation)
      ? situationLabel(c.line, c.situation)
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return [
    `${numberOf(c)} · ${c.name ?? 'sem nome'}`,
    [who, c.plate].filter(Boolean).join(' · '),
    where,
    c.stalled ? `Parado: ${stallReason(c)}` : null,
    ageText(daysSince(c.phaseEnteredAt)),
    c.slaExpired ? 'SLA da fase expirado' : null,
  ]
    .filter(Boolean)
    .join('. ');
}

/** Por que está travado: a parada, ou a situação que trava. */
function stallReason(c: ManagementClaim): string {
  if (c.hold) return analysisHoldLabel(c.hold);
  return c.situation ? situationLabel(c.line, c.situation) : 'sem motivo gravado';
}

/**
 * Um acionamento no mapa: a bolinha com as iniciais, na cor da linha.
 *
 * Cada sinal tem a sua forma, para não depender só de cor: o tracejado é do
 * terceiro, o anel vermelho é o SLA expirado, a pastilha no canto é o travado,
 * o fundo tingido é o aberto nas últimas 24 horas. `dim` apaga quem a busca ou
 * o filtro deixou de fora — ele continua no mapa, onde está.
 */
export function ClaimDot({
  claim: c,
  dim,
  onOpen,
  onPeek,
}: {
  claim: ManagementClaim;
  dim: boolean;
  onOpen: (claim: ManagementClaim) => void;
  /** Passar o mouse ou focar: a dica, ancorada no centro da bolinha. `null` esconde. */
  onPeek: (peek: { claim: ManagementClaim; anchor: DOMRect } | null) => void;
}) {
  const fresh = Date.now() - new Date(c.createdAt).getTime() < FRESH_MS;
  const show = (el: HTMLElement) =>
    onPeek({ claim: c, anchor: el.getBoundingClientRect() });
  return (
    <button
      type="button"
      aria-label={describeClaim(c)}
      onClick={() => onOpen(c)}
      onMouseEnter={(e) => show(e.currentTarget)}
      onMouseLeave={() => onPeek(null)}
      onFocus={(e) => show(e.currentTarget)}
      onBlur={() => onPeek(null)}
      className={cn(
        'relative grid size-7 shrink-0 place-items-center rounded-pilula border-2 bg-superficie text-[0.625rem] font-bold text-texto transition-[transform,opacity] hover:z-10 hover:scale-110 focus-visible:z-10',
        LINE_TONE[c.line].border,
        c.type === 'terceiro' && 'border-dashed',
        fresh && 'bg-primaria-suave',
        c.slaExpired && 'border-erro text-erro ring-2 ring-erro-suave',
        dim && 'opacity-20',
      )}
    >
      {initialsOf(c.name)}
      {c.stalled && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 size-2.5 rounded-pilula border-2 border-superficie bg-alerta"
        />
      )}
    </button>
  );
}

/**
 * A dica da bolinha: quem é, onde está e há quanto tempo — sem abrir a mesa.
 * Mora no mapa, uma só, e segue a bolinha sob o ponteiro ou o foco.
 */
export function ClaimPeek({
  claim: c,
  anchor,
  openable,
}: {
  claim: ManagementClaim;
  anchor: DOMRect;
  /** A pessoa pode abrir a mesa dele? Sem a permissão, a dica não promete o clique. */
  openable: boolean;
}) {
  const days = daysSince(c.phaseEnteredAt);
  return (
    <div
      role="tooltip"
      data-chrome
      style={{ left: anchor.left + anchor.width / 2, top: anchor.bottom + 8 }}
      className="pointer-events-none fixed z-50 max-w-72 -translate-x-1/2 rounded-borda bg-chrome px-3 py-2 text-xs text-chrome-texto shadow-flutuante"
    >
      <p className="text-sm font-semibold">
        {numberOf(c)} · {c.name ?? 'sem nome'}
      </p>
      <p className="text-chrome-texto-suave">
        {c.source === 'periferico'
          ? 'Periféricos'
          : c.type === 'terceiro'
            ? `Terceiro do #${c.parentClaimId}`
            : 'Associado'}
        {c.plate && (
          <>
            {' · '}
            <span className="font-mono tabular-nums">{c.plate}</span>
          </>
        )}
      </p>
      <p className="mt-1">
        {LINE_LABEL[c.line]}, em {stageLabel(c.line, c.phase)}
        {c.situation && !isManagementBlocking(c.line, c.situation)
          ? ` · ${situationLabel(c.line, c.situation)}`
          : ''}
      </p>
      {c.stalled && <p className="font-semibold">Parado: {stallReason(c)}</p>}
      <p className={cn(c.slaExpired && 'font-semibold')}>
        {ageText(days)}
        {c.slaExpired ? ' · SLA da fase expirado' : ''}
      </p>
      {openable && (
        <p className="mt-1 text-chrome-texto-suave">Clique para abrir a mesa</p>
      )}
    </div>
  );
}
